import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import type { OrdenPosDetalleResponse } from '../types/pos.types';
import { formatCurrency } from '../utils/posCalculations';

interface SplitBillDialogProps {
  open: boolean;
  onClose: () => void;
  onSplit: (itemIds: number[]) => void;
  items: OrdenPosDetalleResponse[];
}

export const SplitBillDialog: React.FC<SplitBillDialogProps> = ({
  open,
  onClose,
  onSplit,
  items,
}) => {
  const [selected, setSelected] = useState<number[]>([]);

  const handleToggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleClose = () => {
    setSelected([]);
    onClose();
  };

  const handleSplit = () => {
    onSplit(selected);
    setSelected([]);
  };

  const selectedTotal = items
    .filter((i) => selected.includes(i.idOrdenPosDetalle))
    .reduce((sum, i) => sum + i.total, 0);

  const remainingTotal = items
    .filter((i) => !selected.includes(i.idOrdenPosDetalle))
    .reduce((sum, i) => sum + i.total, 0);

  const activeItems = items.filter((i) => i.estado !== 'CANCELADO');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Separar Cuenta</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Seleccione los items que desea mover a una nueva cuenta:
        </Typography>

        <List dense>
          {activeItems.map((item) => (
            <ListItemButton
              key={item.idOrdenPosDetalle}
              onClick={() => handleToggle(item.idOrdenPosDetalle)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={selected.includes(item.idOrdenPosDetalle)}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2">
                    {item.cantidad}x {item.descripcion}
                  </Typography>
                }
                secondary={`${formatCurrency(item.precioUnitario)} c/u`}
              />
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(item.total)}
              </Typography>
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Nueva cuenta ({selected.length} items):
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary">
            {formatCurrency(selectedTotal)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Cuenta original ({activeItems.length - selected.length} items):
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatCurrency(remainingTotal)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSplit}
          disabled={selected.length === 0 || selected.length === activeItems.length}
        >
          Separar ({selected.length} items)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitBillDialog;
